import type { FuelEntityType } from '@/services';
import type { AccessLevelOption } from '@/utils/accessUtils';

import type { Dictionary } from './base';
import type { Category } from './Category';
import type { AccessLevelType } from './channel';
import type { Schema } from './Schema';
import type { StorageAdapter } from './storage-adapter';
import type { Tag } from './Tag';

export interface OU {
  ouId: string;
  schemas: Schema[];
  parentId: string;
  schemaId?: string;
  ouName: string;
  isEnabled?: boolean;
  isDeleted?: boolean;
  createdBy?: string;
  createdDate?: Date;
  modifiedBy?: string;
  modifiedDate?: Date;
  timezone?: string;
  timezoneAbbreviation?: string;
  dashboardUrl?: string;
  analyticsElasticSearchUrl?: string;
  ouSlug?: string;
  ouType?: string;
  ouPath?: string;
  defaultVariantCodes?: string[];
  categories?: Category[];
  storageAdapters?: StorageAdapter[];
  tags?: Tag[];
  id?: string;
  entityType: string;
  metaData?: OuMetaData;
}

export type WorkflowFieldTypes = 'String' | 'DateTime' | 'Boolean' | 'Enum';

export interface WorkflowInfo {
  fields: Dictionary<string>;
  commentFileS3Paths?: string[];
}

export interface WorkflowConfig {
  enableWorkflows: boolean;
  groups?: WorkflowGroup[];
  fields?: WorkflowField[];

  // UI Tree Fields
  entities: WorkflowEntity[];

  // UI Form props
  selectedEntity?: WorkflowEntity;
  selectedGroup?: WorkflowGroup;
}

export interface WorkflowGroup {
  id: string;
  entityType: FuelEntityType;
  name: string;
  description?: string;

  // UI Tree Fields
  childFields?: WorkflowField[];

  // UI props for WorkflowDetails
  isEnabled?: boolean;
}

export interface WorkflowField {
  id?: string;
  entityType?: string;
  group?: string;
  name?: string;
  description?: string;
  type?: WorkflowFieldTypes;
  allowedValues?: WorkflowAllowedValue[];
  template?: string;

  // UI prop for textarea
  partialId?: string;
  allowedValuesText?: string;

  // UI props for WorkflowDetails
  value?: string;
}

export interface WorkflowEntity {
  id?: FuelEntityType;
  name?: string;
  childGroups?: WorkflowGroup[];
}

export interface WorkflowAllowedTypes {
  id?: WorkflowFieldTypes;
  name?: string;
}

export interface WorkflowAllowedValue {
  id?: string;
  name?: string;
}

/** Cross-OU sharing behavior for OU defaults and per-asset overrides (matches backend ContentSharingBehaviors). */
export type ContentSharingBehaviors = 'DoNotShare' | 'ShareWithParentPortal' | null;

/** Cross-OU content sharing configuration; used on OuMetaData and clip ItemData (matches backend ContentSharingConfig). */
export interface ContentSharingConfig {
  behavior?: ContentSharingBehaviors;
}

export type ContentSharingBehaviorOption = {
  label: string;
  value: ContentSharingBehaviors;
};

/** Labels for OU / asset sharing behavior dropdowns (values match backend enum). */
export const CONTENT_SHARING_BEHAVIOR_OPTIONS: ContentSharingBehaviorOption[] = [
  { label: 'Do Not Share', value: 'DoNotShare' },
  { label: 'Share With Parent Partner', value: 'ShareWithParentPortal' },
];

export interface OuMetaData {
  ouCodeName?: string;
  ouUrl?: string;
  defaultTargetDuration?: number;
  defaultFPS?: string;
  ingestVariant?: string;
  ingestVariants?: string[];
  defaultVariants?: string[];
  allowedVariants?: string[];
  speechToTextOnIngest?: boolean;
  /** Gates automatic AI story/sub-clip recommendations for ingested live broadcast clips. */
  enableAIGeneratedSubclips?: boolean;
  createCCOnIngest?: boolean;
  playerEmbedCodeTemplate?: string;
  externalServiceConnectors?: ExternalServiceConnector[];
  expiredAssetAutoRemove?: number;
  showCreateCCButton?: boolean;
  automatedCleanupSettings?: AutomatedCleanupSettings;
  showRobustSubClippingOption?: boolean;
  seConnectionInfo: StreamingEngineConnectionInfo | null;
  adConfiguration: OUAdConfiguration | null;
  defaultChannelRefreshControls: ChannelRefreshControl | null;
  enableSite: boolean;
  siteId: string;
  defaultImages: Record<string, string>;
  isTrickPlayEnabled: boolean;
  ouFeatures?: OUFeatures;
  enablePaywall: boolean;
  defaultAccessLevel: AccessLevelType;
  enableLiveTranscription: boolean;
  allowMultipleCategories: boolean;
  workflowConfig?: WorkflowConfig;
  maxLiveClipMinutes?: number;
  /**
   * When true, users may edit Sharing Behavior on eligible assets (new shares).
   * When false, existing per-asset contentSharing is still shown (read-only) and the share icon still reflects ShareWithParentPortal.
   */
  enableCrossOuSharing?: boolean;
  /** OU-level default when an asset has no per-item contentSharing override. */
  contentSharing?: ContentSharingConfig;
  // UI extra options
  defaultAccessLevelOptions: AccessLevelOption[];
}

export interface OUFeatures {
  customerType?: string | null;
  navigation: NavigationOptions;
}

export interface NavigationOptions {
  accountActivity: boolean;
  billing: boolean;
  cmsUsers: boolean;
  profile: boolean;
  assetLibrary: boolean;
  channelManager: boolean;
  content: boolean;
  movies: boolean;
  series: boolean;
  dashboards: boolean;
  analytics: boolean;
  destinations: boolean;
  health: boolean;
  monitoringDashboard: boolean; // 'streamMonitoring'
  usage: boolean;
  'streaming-analytics': boolean;
  epg: boolean;
  live: boolean;
  appEvents: boolean;
  liveGuide: boolean;
  inputs: boolean;
  livePrograms: boolean; // 'streamKeySetup'
  liveBroadcast: boolean;
  liveBroadcasts: boolean;
  inputSources: boolean;
  vnms: boolean;
  'vnms-admin': boolean;
  'vnms-user-view': boolean;
  marketing: boolean;
  firebase: boolean;
  mailchimp: boolean;
  pushNotifications: boolean;
  'monetization-dropdown': boolean;
  monetization: boolean;
  ads: boolean;
  products: boolean;
  productCoupons: boolean;
  settings: boolean;
  adConfig: boolean;
  'settings-admin': boolean;
  advancedAdmin: boolean;
  transitionPackages: boolean; // 'automatedTransitions'
  categories: boolean;
  globalInterstitials: boolean;
  admin: boolean;
  mrssFeedConfig: boolean;
  mrssMapper: boolean;
  ouNavigation: boolean;
  site: boolean;
  uploadAssets: boolean;
  'account-dropdown': boolean;
  account: boolean;
  appUsers: boolean;
  users: boolean;
  workflowConfig: boolean;
  viewNexaAssistant: boolean;
  onboarding: boolean;
  help: boolean;
  divider: boolean;
  eventWorkflows: boolean;
}

export interface OUAdConfiguration {
  allowExpertMode: boolean;
  serverSideAdPlatformId: string | null;
  ssaiSupplyTagId: string | null;
  clientSideAdPlatformId: string | null;
  csaiSupplyTagId: string | null;
  demandPlatformId: string | null;
  demandPartnerId: string | null;
}

export interface ExternalServiceConnector {
  serviceName: string;
  connectionParams: ConnectionParams;
}

export interface ConnectionParams {
  irisTVOUClientToken: string;
}

export interface StreamingEngineConnectionInfo {
  baseApiUrl: string;
  baseContentPath: string;
  apiKey: string;
}

export interface ChannelRefreshControl {
  minTimeBetweenRefresh?: number | null;
  maxTimeBetweenRefresh?: number | null;
}

export interface FeedConfiguration {
  id: string;
  ouId: string;
  enabled: boolean;
  feedName: string;
  feedUrl: string;
  paginationConfig: PaginationConfiguration | null;
  defaultTags: string[];
  defaultCategories: string[];
  extractExternalServiceMeta: boolean;
  healthStatusSummary: string;

  // ui properties
  actions?: string;
  categoryString?: string;
  tagString?: string;
}
export interface FeedTask {
  id: string;
  ouId: string;
  feedConfig: FeedConfiguration;
  isOnDemand: boolean;
  paginationState: PaginationConfiguration;
  pageCount: number;
  processedOn: Date;
}
export enum PaginationMethod {
  QueryParam = 'QueryParam',
  SyndicationChannelElementLink = 'SyndicationChannelElementLink',
  None = 'None',
  TemplatedFeedUrl = 'TemplatedFeedUrl',
}

export interface PaginationConfiguration {
  method: PaginationMethod;
  parameterName: string;
  initialValue: string;
  pageLimit: number;
}

export interface AutomatedCleanupSettings {
  automatedCleanupEnabled: boolean;
  softDeleteAssetsAfterDuration: string | null;
  hardDeleteAssetsAfterDuration: string | null;
  softDeleteIgnoreTags: string[];
  softDeleteIgnoreCategories: Category[];
}
